// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import { StrictHttpResponse } from '../../../api/remote-api/.portal.generated/strict-http-response';

// signature of the api call methods generated by https://github.com/cyclosproject/ng-openapi-gen
export type ApiFnOptional<P, R> = (
  http: HttpClient,
  rootUrl: string,
  params?: P,
  context?: HttpContext
) => Observable<StrictHttpResponse<R>>;
export type ApiFnRequired<P, R> = (
  http: HttpClient,
  rootUrl: string,
  params: P,
  context?: HttpContext
) => Observable<StrictHttpResponse<R>>;

export type HttpMethod = 'get' | 'post' | 'put' | 'delete' | 'patch';
/**
 * annotates the api functiontype with the path that ng-openapi-gen actually ads
 */
export type ApiFnRequiredWithPath<P, R> = ApiFnRequired<P, R> & {
  PATH: string;
  METHOD: string;
};

interface Operation<P, R> {
  op: ApiFnRequiredWithPath<P, R>;
  handler: (
    params: P
  ) =>
    | ReturnType<ApiFnRequiredWithPath<P, R>>
    | Promise<ReturnType<ApiFnRequiredWithPath<P, R>>>;
}

/**
 * collects list of all mocked operations
 */
export const mocked: Operation<unknown, unknown>[] = [];

/**
 * adds a mocked operation to the list above
 */
export function mockOperation<
  T extends ApiFnRequiredWithPath<P, R>,
  P = T extends ApiFnRequiredWithPath<infer P, unknown> ? P : unknown,
  R = T extends ApiFnRequiredWithPath<P, infer R> ? R : unknown
>(op: T, handler: Operation<P, R>['handler']): void {
  mocked.push({
    // eslint-disable-next-line
    // @ts-ignore
    op,
    // eslint-disable-next-line
    // @ts-ignore
    handler,
  });
}

/**
 * see if we have an mocked operation given a url
 * id did not find a good OTS url matcher, i am sure it exsits
 */
export function findOperation(
  method: HttpMethod,
  url: string
):
  | (Operation<unknown, unknown> & { params: Record<string, string | number> })
  | undefined {
  const m = new RegExp(method, 'i');
  const match = mocked.find(
    ({ op }) => op.METHOD.match(m) && matchUrl(url, op.PATH)
  );

  if (match) {
    const params = matchUrl(url, match.op.PATH)!;
    return { ...match, params };
  }
  return undefined;
}

/**
 * VERY lean path matcher and parm extractor
 * NOT SAFE for production use, only used during testing for mocking API
 * supports: path params and query params (int/string)
 * params are merged into a flat object and passt to the handler
 */
function matchUrl(
  url: string,
  pattern: string
): Record<string, string> | undefined {
  try {
    // host ist required for url parsing to work
    const { pathname, searchParams } = new URL(url, 'http://dont-care.com');
    const m = pathname.match(
      new RegExp(pattern.replace(/\{([a-zA-Z0-9]+)\}/g, '(?<$1>[^/]+)'), 'i')
    );

    return m
      ? parseParamValues({
          ...searchParamsToObject(searchParams),
          ...(m.groups || {}),
        })
      : undefined;
  } catch (e) {
    console.log('regexp probably broken', e);
  }
}

function searchParamsToObject(searchParams: URLSearchParams) {
  const obj: Record<string, string | string[]> = {};
  for (const [k, v] of searchParams) {
    if (k in obj) {
      if (Array.isArray(obj[k])) {
        (obj[k] as string[]).push(v);
      } else {
        obj[k] = [obj[k] as string, v];
      }
    } else {
      obj[k] = v;
    }
  }
  return obj;
}
function parseParamValues(
  params: Record<string, string | string[]>
): Record<string, string> {
  const numberCoersion = (v: string) => {
    v = decodeURIComponent(v);
    return !Number.isNaN(parseInt(v)) ? parseInt(v) : v;
  };

  return Object.entries(params).reduce(
    (acc, [k, v]) => ({
      ...acc,
      [k]: Array.isArray(v) ? v.map(numberCoersion) : numberCoersion(v),
    }),
    {}
  );
}

/**
 * utility to create a successful json response,
 * if passed body is undefined, 404 is returned
 * @param body body to return
 * @returns
 */
export function JSONResponse<T>(
  body?: T,
  errorOnUndefined = true
): Observable<HttpResponse<T>> {
  return body || !errorOnUndefined
    ? of(new HttpResponse(structuredClone({ body })))
    : ErrorResponse(404);
}

/**
 * utility to create a failed json response,
 * if passed body is undefined, 404 is returned
 * @param body body to return
 * @returns
 */
export function ErrorResponse<T>(
  status: number,
  body?: T,
  statusText = { [404]: 'Resource not found' }[status]
): Observable<HttpResponse<T>> {
  return of(new HttpResponse({ status, body, statusText }));
}
